import { useSerialBootstrap } from '@/features/serial/useSerialBootstrap';
import { HelperRequiredDialog } from '@/features/helper/HelperRequiredDialog';
import { HelperStartDialog } from '@/features/helper/HelperStartDialog';
import { UploadResultDialog } from '@/features/upload/UploadResultDialog';
import { ArduinoSetupDialog } from '@/features/helper/ArduinoSetupDialog';
import { useHelperStartupPrompt, useHelperStartDialogAutoClose } from '@/features/helper/useHelperStartupPrompt';
import { BlocklyWorkspace, type BlocklyWorkspaceHandle } from '@/features/workspace/BlocklyWorkspace';
import { WorkspaceControls } from '@/features/workspace/WorkspaceControls';
import {
  buildProjectFile,
  downloadProjectFile,
  readProjectFile,
  sanitizeProjectFilename,
  type SkyroverProjectFile,
} from '@/features/project/projectFile';
import { SaveProjectDialog } from '@/features/project/SaveProjectDialog';
import {
  projectNameFromFileName,
  promptOpenProjectLocation,
  promptSaveProjectLocation,
  readProjectFromHandle,
  supportsNativeProjectFiles,
  writeProjectToHandle,
} from '@/features/project/projectStorage';
import { useBoardStore } from '@/state/boardStore';
import { useConsoleStore } from '@/state/consoleStore';
import { useProjectStore } from '@/state/projectStore';
import { useSettingsStore } from '@/state/settingsStore';
import { useWorkspaceStore } from '@/state/workspaceStore';
import { Header } from '@/ui/layout/Header';
import { RightPanel } from '@/ui/layout/RightPanel';
import { StatusBar } from '@/ui/layout/StatusBar';
import { SKYROVER_COLORS } from '@/theme/skyroverTheme';
import { getBoard } from '@skyrover/board-definitions';
import { getExampleById } from '@skyrover/examples';
import { Box } from '@mui/material';
import { useCallback, useRef, useState } from 'react';

export function AppShell() {
  useSerialBootstrap();
  useHelperStartupPrompt();
  useHelperStartDialogAutoClose();
  const workspaceRef = useRef<BlocklyWorkspaceHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setBoardId = useBoardStore((s) => s.setBoardId);
  const boardId = useBoardStore((s) => s.boardId);
  const isDirty = useWorkspaceStore((s) => s.isDirty);
  const markClean = useWorkspaceStore((s) => s.markClean);
  const projectName = useSettingsStore((s) => s.projectName);
  const projectManifest = useSettingsStore((s) => s.projectManifest);
  const resetProject = useSettingsStore((s) => s.resetProject);
  const setProjectManifest = useSettingsStore((s) => s.setProjectManifest);
  const fileHandle = useProjectStore((s) => s.fileHandle);
  const savedFileName = useProjectStore((s) => s.savedFileName);
  const setSaveTarget = useProjectStore((s) => s.setSaveTarget);
  const clearSaveTarget = useProjectStore((s) => s.clearSaveTarget);
  const addMessage = useConsoleStore((s) => s.addMessage);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogTitle, setSaveDialogTitle] = useState('Save Project');

  const confirmDiscardChanges = useCallback(() => {
    if (!isDirty) {
      return true;
    }
    return window.confirm('You have unsaved changes. Continue without saving?');
  }, [isDirty]);

  const handleRegenerateCode = useCallback(() => {
    workspaceRef.current?.regenerateCode();
  }, []);

  const loadProject = useCallback(
    (project: SkyroverProjectFile) => {
      const board = getBoard(project.manifest.boardId);
      if (!board) {
        throw new Error(`Unknown board: ${project.manifest.boardId}`);
      }
      setBoardId(project.manifest.boardId);
      setProjectManifest(project.manifest);
      workspaceRef.current?.loadWorkspaceState(project.workspace);
      markClean();
    },
    [markClean, setBoardId, setProjectManifest],
  );

  const buildCurrentProject = useCallback(
    (name: string) => {
      const workspace = workspaceRef.current?.getWorkspaceState();
      if (!workspace) {
        return null;
      }
      return buildProjectFile(projectManifest, workspace, boardId, name);
    },
    [boardId, projectManifest],
  );

  const finalizeSave = useCallback(
    (
      project: SkyroverProjectFile,
      target: { fileHandle: FileSystemFileHandle | null; savedFileName: string | null },
      message: string,
    ) => {
      setSaveTarget(target.fileHandle, target.savedFileName);
      setProjectManifest(project.manifest);
      markClean();
      addMessage('success', message);
    },
    [addMessage, markClean, setProjectManifest, setSaveTarget],
  );

  const saveToExistingTarget = useCallback(async () => {
    const project = buildCurrentProject(projectName);
    if (!project) {
      return;
    }

    if (fileHandle) {
      await writeProjectToHandle(fileHandle, project);
      finalizeSave(project, { fileHandle, savedFileName: fileHandle.name }, `Project saved to ${fileHandle.name}.`);
      return;
    }

    if (savedFileName) {
      downloadProjectFile(project, savedFileName);
      finalizeSave(
        project,
        { fileHandle: null, savedFileName },
        `Project saved as ${savedFileName}.`,
      );
    }
  }, [buildCurrentProject, fileHandle, finalizeSave, projectName, savedFileName]);

  const saveToNewLocation = useCallback(
    async (
      suggestedName: string,
      options: { successPrefix: string; dialogTitle: string },
    ) => {
      const workspace = workspaceRef.current?.getWorkspaceState();
      if (!workspace) {
        return;
      }

      if (supportsNativeProjectFiles()) {
        const handle = await promptSaveProjectLocation(suggestedName);
        if (!handle) {
          return;
        }
        const name = projectNameFromFileName(handle.name);
        const project = buildProjectFile(projectManifest, workspace, boardId, name);
        await writeProjectToHandle(handle, project);
        finalizeSave(
          project,
          { fileHandle: handle, savedFileName: handle.name },
          `${options.successPrefix} ${handle.name}.`,
        );
        return;
      }

      setSaveDialogTitle(options.dialogTitle);
      setSaveDialogOpen(true);
    },
    [boardId, finalizeSave, projectManifest],
  );

  const handleNewProject = useCallback(() => {
    if (!confirmDiscardChanges()) {
      return;
    }
    clearSaveTarget();
    resetProject(boardId);
    workspaceRef.current?.clearWorkspace();
    markClean();
    addMessage('info', 'New project created.');
  }, [addMessage, boardId, clearSaveTarget, confirmDiscardChanges, markClean, resetProject]);

  const handleSaveProject = useCallback(async () => {
    try {
      if (fileHandle || savedFileName) {
        await saveToExistingTarget();
        return;
      }
      await saveToNewLocation(projectName, {
        successPrefix: 'Project saved to',
        dialogTitle: 'Save Project',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save project.';
      addMessage('error', message);
    }
  }, [addMessage, fileHandle, projectName, saveToExistingTarget, saveToNewLocation, savedFileName]);

  const handleSaveProjectAs = useCallback(async () => {
    try {
      await saveToNewLocation(projectName, {
        successPrefix: 'Project saved as',
        dialogTitle: 'Save Project As',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save project.';
      addMessage('error', message);
    }
  }, [addMessage, projectName, saveToNewLocation]);

  const handleSaveProjectConfirm = useCallback(
    async (name: string) => {
      const workspace = workspaceRef.current?.getWorkspaceState();
      if (!workspace) {
        return;
      }

      try {
        const project = buildProjectFile(projectManifest, workspace, boardId, name);
        const fileName = sanitizeProjectFilename(name);

        if (supportsNativeProjectFiles()) {
          const handle = await promptSaveProjectLocation(name);
          if (!handle) {
            return;
          }
          const resolvedName = projectNameFromFileName(handle.name);
          const resolvedProject = buildProjectFile(projectManifest, workspace, boardId, resolvedName);
          await writeProjectToHandle(handle, resolvedProject);
          finalizeSave(
            resolvedProject,
            { fileHandle: handle, savedFileName: handle.name },
            `Project saved to ${handle.name}.`,
          );
        } else {
          downloadProjectFile(project, fileName);
          finalizeSave(
            project,
            { fileHandle: null, savedFileName: fileName },
            `Project saved as ${fileName}.`,
          );
        }

        setSaveDialogOpen(false);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save project.';
        addMessage('error', message);
      }
    },
    [addMessage, boardId, finalizeSave, projectManifest],
  );

  const handleOpenProjectClick = useCallback(async () => {
    if (!confirmDiscardChanges()) {
      return;
    }

    try {
      if (supportsNativeProjectFiles()) {
        const handle = await promptOpenProjectLocation();
        if (!handle) {
          return;
        }
        const project = await readProjectFromHandle(handle);
        loadProject(project);
        setSaveTarget(handle, handle.name);
        addMessage('success', `Opened project "${project.manifest.name}".`);
        return;
      }

      fileInputRef.current?.click();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to open project.';
      addMessage('error', message);
    }
  }, [addMessage, confirmDiscardChanges, loadProject, setSaveTarget]);

  const handleProjectFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) {
        return;
      }

      try {
        const project = await readProjectFile(file);
        loadProject(project);
        setSaveTarget(null, file.name);
        addMessage('success', `Opened project "${project.manifest.name}".`);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to open project.';
        addMessage('error', message);
      }
    },
    [addMessage, loadProject, setSaveTarget],
  );

  const handleLoadExample = useCallback(
    (exampleId: string) => {
      if (!confirmDiscardChanges()) {
        return;
      }
      const example = getExampleById(exampleId);
      if (!example) {
        return;
      }
      clearSaveTarget();
      setBoardId(example.boardId);
      resetProject(example.boardId);
      workspaceRef.current?.loadWorkspaceState(example.workspaceState);
      markClean();
      addMessage('info', `Loaded example "${example.name}".`);
    },
    [addMessage, clearSaveTarget, confirmDiscardChanges, markClean, resetProject, setBoardId],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".skyrover,application/json"
        hidden
        onChange={handleProjectFileSelected}
      />

      <Header
        onNewProject={handleNewProject}
        onOpenProject={handleOpenProjectClick}
        onSaveProject={handleSaveProject}
        onSaveProjectAs={handleSaveProjectAs}
        onLoadExample={handleLoadExample}
        onRegenerateCode={handleRegenerateCode}
      />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', bgcolor: SKYROVER_COLORS.workspace }}>
          <BlocklyWorkspace ref={workspaceRef} />
          <WorkspaceControls workspaceRef={workspaceRef} />
        </Box>

        <RightPanel />
      </Box>

      <StatusBar />

      <SaveProjectDialog
        open={saveDialogOpen}
        initialName={projectName}
        title={saveDialogTitle}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveProjectConfirm}
      />

      <HelperRequiredDialog />
      <HelperStartDialog />
      <ArduinoSetupDialog />
      <UploadResultDialog />
    </Box>
  );
}
